import { useCallback } from 'react';
import { useApolloClient } from '@apollo/react-hooks';

import {
  GET_CANONICAL_PRODUCT,
  GET_PACKAGE_BY_CODE,
  GET_PRODUCT_BY_CODE,
  GET_PRODUCT_SET_BY_CODE
} from '../../graphql/product-detail-queries';

import { getVehicleInput } from '../../utils/fitment/fitment';
import { getTreadwellGQLInput } from '../../utils/treadwell/treadwell';

import { PRERENDERING_DEFAULT_STORE } from '../../constants/store';

function useProductDetail(myStore) {
  const client = useApolloClient();

  const getProductDetail = useCallback(
    async ({
      fitment,
      store,
      isTreadwellFlow,
      treadwellPreferences,
      productCode,
      packageCode,
      rearProductCode,
      productSlug,
      canonicalProductType,
      canonicalFilters,
      canonicalProductCodes,
      shouldFetchAllCanonicalData
    }) => {
      const isStaggered = Boolean(rearProductCode);
      const isPrebuiltPackage = Boolean(packageCode);
      const isCanonicalSearch = Boolean(!productCode && !packageCode);

      function getCanonicalQueryVariables() {
        return {
          id: productSlug,
          options: {
            filters: canonicalFilters,
            productCodes: canonicalProductCodes || []
          },
          productType: canonicalProductType,
          storeCode: store?.code,
          vehicleInfo: !shouldFetchAllCanonicalData
            ? getVehicleInput(fitment)
            : null
        };
      }

      function getQueryVariables() {
        let treadwellRequest = null;
        let vehicleInfo = null;
        const zipCode = (
          window.isPreRendering ? PRERENDERING_DEFAULT_STORE : myStore
        )?.address?.postalCode;
        const storeCode = store?.code;

        const isStaggered = Boolean(rearProductCode);
        const isPrebuiltPackage = Boolean(packageCode);

        if (fitment) {
          vehicleInfo = getVehicleInput(fitment) || null;
          treadwellRequest = isTreadwellFlow
            ? getTreadwellGQLInput(fitment, treadwellPreferences)
            : null;
        }

        const variables = {
          storeCode,
          treadwellRequest,
          vehicleInfo,
          weatherZipCode: isTreadwellFlow ? treadwellRequest?.zipCode : zipCode
        };

        if (isStaggered) {
          variables['frontCode'] = productCode;
          variables['rearCode'] = rearProductCode;
        } else if (isPrebuiltPackage) {
          variables['packageCode'] = packageCode;
        } else {
          variables['productCode'] = productCode;
        }

        return variables;
      }

      const queryVariables = isCanonicalSearch
        ? getCanonicalQueryVariables()
        : getQueryVariables();

      let query = GET_PRODUCT_BY_CODE;
      let queryType = 'byCode';
      if (isStaggered) {
        query = GET_PRODUCT_SET_BY_CODE;
        queryType = 'setByCode';
      } else if (isPrebuiltPackage) {
        query = GET_PACKAGE_BY_CODE;
        queryType = 'packageByCode';
      } else if (isCanonicalSearch) {
        query = GET_CANONICAL_PRODUCT;
        queryType = 'canonicalById';
      }

      const { data, errors } = await client.query({
        fetchPolicy: isCanonicalSearch ? 'network-only' : 'cache-first',
        query,
        variables: queryVariables
      });

      return {
        errors: errors,
        product: data?.product?.[queryType],
        queryType,
        recommendations: data?.recommendationQueries
      };
    },
    [client, myStore]
  );

  return {
    getProductDetail
  };
}

export default useProductDetail;
